import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Library } from "lucide-react";
import { useBlacklistTemplates, useImportBlacklistTemplates, mapRpcError, type BlacklistTemplate } from "@/hooks/useGroupBlacklist";
import { useToast } from "@/hooks/use-toast";

const CATEGORY_LABELS: Record<BlacklistTemplate["category"], string> = {
  political: "Política",
  religious: "Religião",
  nsfw: "Conteúdo adulto",
  health_claims: "Saúde",
  financial_advice: "Financeiro",
  violence: "Violência",
  personal_data: "Dados pessoais",
};

export function ImportTemplateDialog({ groupId }: { groupId: string }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data: templates, isLoading } = useBlacklistTemplates();
  const importMut = useImportBlacklistTemplates(groupId);
  const { toast } = useToast();

  const grouped = useMemo(() => {
    const out: Record<string, BlacklistTemplate[]> = {};
    (templates ?? []).forEach((t) => {
      (out[t.category] ??= []).push(t);
    });
    return out;
  }, [templates]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) return;
    try {
      const result = await importMut.mutateAsync(Array.from(selected));
      toast({
        title: "Template importado",
        description: `${result.imported_count} tópico(s) bloqueados${result.skipped_count > 0 ? ` · ${result.skipped_count} ignorados` : ""}.`,
      });
      setSelected(new Set());
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Falha ao importar", description: mapRpcError(err) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Library className="h-4 w-4 mr-2" />
          Importar template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Biblioteca de tópicos sensíveis</DialogTitle>
          <DialogDescription>
            Selecione um ou mais tópicos da curadoria SYRA para bloquear neste grupo.
            Você pode reativar/desativar cada um depois.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold mb-2">
                    {CATEGORY_LABELS[category as BlacklistTemplate["category"]] ?? category}
                  </h4>
                  <div className="space-y-2">
                    {items.map((t) => (
                      <label
                        key={t.id}
                        className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-accent"
                      >
                        <Checkbox
                          checked={selected.has(t.id)}
                          onCheckedChange={() => toggle(t.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{t.topic_label}</span>
                            <Badge variant={t.default_severity === "hard" ? "default" : "secondary"} className="text-xs">
                              {t.default_severity === "hard" ? "rígido" : "leve"}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{t.description_pt}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={selected.size === 0 || importMut.isPending}>
            {importMut.isPending ? "Importando..." : `Importar ${selected.size > 0 ? `(${selected.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
